import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Menu } from "@devices/components/Header/Menu";
import { Box, Theme, Typography } from "@mui/material";
import { CheckboxField } from "@shared/form/CheckboxField";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { Table } from "@tanstack/react-table";

import DropdownContainer from "../shared/Dropdown/DropdownContainer";

import { DeviceTableData } from "./DeviceTable.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    box: {
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
    },
    dropdownBox: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        width: "100%",
        padding: theme.spacing(3, 5),
    },
    checkboxContainer: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1),
    },
    title: {
        display: "inline",
        fontWeight: 700,
    },
    titleContainer: {
        textAlign: "center",
        marginBottom: theme.spacing(1),
    },
    outerContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
}));

type Props = {
    value: string;
    onChange: (value: string) => void;
    onClear: () => void;
    table: Table<DeviceTableData>;
};
export const ManagementSection = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const anchorRefManage = React.useRef<HTMLButtonElement | null>(null);
    const [columnsMenuOpen, setColumnsMenuOpen] = React.useState(false);

    // exclude actions columns from the list of columns where visibility can be toggled
    const tableColumns = props.table.getAllColumns().filter((column) => column.columnDef.id !== "moveLicenseCheckbox");
    return (
        <Box className={classes.outerContainer}>
            <Box className={classes.container}>
                <QueryBuilderField
                    value={props.value}
                    placeholder={t("common:button.search")}
                    onChange={props.onChange}
                    onClear={props.onClear}
                    options={[
                        { key: "device.lastState.build.model", label: t("pages:devicePage.filters.deviceModel") },
                        { key: "groupName", label: t("pages:devicePage.filters.groupName") },
                        { key: "device.serial", label: t("pages:devicePage.filters.serial") },
                        { key: "device.lastState.packages.versionCode", label: t("pages:devicePage.filters.applicationVersion") },
                        { key: "device.lastState.packages.package", label: t("pages:devicePage.filters.applicationPackage") },
                        { key: "chargeId", label: t("pages:devicePage.filters.chargeId") },
                    ]}
                />

                <Box className={classes.box}>
                    <Menu anchorRef={anchorRefManage} onSelectColumns={() => setColumnsMenuOpen(true)} />
                    <DropdownContainer
                        hideTrigger
                        anchorRef={anchorRefManage}
                        open={columnsMenuOpen}
                        onOpenChange={setColumnsMenuOpen}
                        listProps={{ eventType: "onKeyDown" }}
                    >
                        <Box className={classes.dropdownBox}>
                            {tableColumns.map((column) => (
                                <Box key={column.id}>
                                    <div className={classes.titleContainer}>
                                        <Typography variant="body1" className={classes.title}>
                                            {column.id}
                                        </Typography>
                                    </div>
                                    {column.getLeafColumns().map((leafColumn) => (
                                        <Box key={leafColumn.id} className={classes.checkboxContainer}>
                                            <CheckboxField
                                                checked={leafColumn.getIsVisible()}
                                                onChange={() => leafColumn.toggleVisibility()}
                                                label={
                                                    (leafColumn.columnDef.header as string) ||
                                                    t("pages:devicePage.table.header.indicator")
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </DropdownContainer>
                </Box>
            </Box>
        </Box>
    );
};
